import React from "react";
import { Snackbar, Box } from "@material-ui/core";
import { SnackBar, Button } from "@orijinworks/frontend-commons";
import { CONTENT_TABS } from "./constants";
import { BrowseView } from "./views/BrowseView";
import { TemplateLibraryView } from "./views/TemplateLibraryView";
import { TemplateEditorView } from "./views/TemplateEditorView";
import { WizardView } from "./views/WizardView";
import { useLocationConfigManagerHook } from "./hooks/useLocationConfigManagerHook";
import { useStyles } from "./location-config-manager.style";

export default function LocationConfigManager() {
  const classes = useStyles();
  const {
    activeTab,
    setActiveTab,
    searchText,
    setSearchText,
    selectedLocation,
    setSelectedLocation,
    expandedNodes,
    toggleExpanded,
    handleTabClick,
    toast,
    clearToast,
    config,
    locationNode,
    locationChain,
    crud,
    template,
    wizard,
    saveTemplateWithNotify,
    deleteTemplateWithNotify,
    toggleTemplateExpandedService,
    toggleTemplateExpandedResource,
    toggleWizardExpanded,
    locationsTree,
    configLoading,
    configError,
    servicesRef,
    resourcesRef,
  } = useLocationConfigManagerHook();

  const renderTabContent = () => {
    if (activeTab === "browse") {
      return (
        <BrowseView
          search={searchText}
          setSearch={setSearchText}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          expandedNodes={expandedNodes}
          toggleExpanded={toggleExpanded}
          config={config}
          locationNode={locationNode}
          locationChain={locationChain}
          openTemplateFromLocation={template.openTemplateFromLocation}
          wizardReset={wizard.wizardReset}
          setTab={setActiveTab}
          editId={crud.editId}
          editType={crud.editType}
          editData={crud.editData}
          setEditData={crud.setEditData}
          deleteConfirmation={crud.deleteConfirmation}
          setDeleteConfirmation={crud.setDeleteConfirmation}
          startEdit={crud.startEdit}
          cancelEdit={crud.cancelEdit}
          saveEdit={crud.saveEdit}
          doDelete={crud.doDelete}
          locations={locationsTree}
          configLoading={configLoading}
          configError={configError}
          servicesRef={servicesRef}
          resourcesRef={resourcesRef}
        />
      );
    }
    if (activeTab === "wizard") {
      return (
        <WizardView
          wizardStep={wizard.wizardStep}
          wizardDone={wizard.wizardDone}
          wizardApplyErrors={wizard.wizardApplyErrors}
          wizardUnresolvedServiceIds={wizard.wizardUnresolvedServiceIds}
          wizardUnresolvedResourceIds={wizard.wizardUnresolvedResourceIds}
          wizardTemplateId={wizard.wizardTemplateId}
          setWizardTemplateId={wizard.setWizardTemplateId}
          wizardChecked={wizard.wizardChecked}
          wizardExpanded={wizard.wizardExpanded}
          wizardSearch={wizard.wizardSearch}
          setWizardSearch={wizard.setWizardSearch}
          wizardMode={wizard.wizardMode}
          setWizardMode={wizard.setWizardMode}
          wizardSelectedTemplate={wizard.wizardSelectedTemplate}
          wizardLeafs={wizard.wizardLeafs}
          wizardOverlap={wizard.wizardOverlap}
          wizardNext={wizard.wizardNext}
          wizardPrev={wizard.wizardPrev}
          wizardCanGo={wizard.wizardCanGo}
          wizardReset={wizard.wizardReset}
          wizardCheck={wizard.wizardCheck}
          applyWizard={wizard.applyWizard}
          wizardStartApply={wizard.wizardStartApply}
          setTab={setActiveTab}
          templates={template.templates}
          openNewTemplate={template.openNewTemplate}
          toggleWizardExpanded={toggleWizardExpanded}
          locations={locationsTree}
          servicesRef={servicesRef}
          resourcesRef={resourcesRef}
        />
      );
    }
    if (activeTab === "templates" && template.editingTemplate) {
      return (
        <TemplateEditorView
          resourcesRef={resourcesRef}
          servicesRef={servicesRef}
          editingTemplate={template.editingTemplate}
          setEditingTemplate={template.setEditingTemplate}
          onBack={() => template.setEditingTemplate(null)}
          onSave={saveTemplateWithNotify}
          templateExpandedServices={template.templateExpandedServices}
          templateExpandedResources={template.templateExpandedResources}
          toggleExpandedService={toggleTemplateExpandedService}
          toggleExpandedResource={toggleTemplateExpandedResource}
          templateUpdateService={template.templateUpdateService}
          templateRemoveService={template.templateRemoveService}
          templateAddService={template.templateAddService}
          templateUpdateResource={template.templateUpdateResource}
          templateRemoveResource={template.templateRemoveResource}
          templateAddResource={template.templateAddResource}
        />
      );
    }
    if (activeTab === "templates") {
      return (
        <TemplateLibraryView
          templates={template.templates}
          openNewTemplate={template.openNewTemplate}
          openEditTemplate={template.openEditTemplate}
          deleteTemplate={deleteTemplateWithNotify}
          wizardStartApply={wizard.wizardStartApply}
        />
      );
    }
    return null;
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.tabBar}>
        <Box className={classes.tabGroup}>
          {CONTENT_TABS.map((contentTab, index) => {
            const isActive = activeTab === contentTab.id;
            return (
              <React.Fragment key={contentTab.id}>
                {index > 0 && <Box className={classes.tabSeparator} aria-hidden />}
                <Button
                  variant={isActive ? "contained" : "text"}
                  size="small"
                  onClick={() => handleTabClick(contentTab.id)}
                  className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ""}`}
                >
                  {contentTab.label}
                </Button>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.contentArea}>
        {renderTabContent()}
      </Box>
      {toast && (
        <Snackbar
          open
          autoHideDuration={2800}
          onClose={clearToast}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <SnackBar
            isShown
            type={toast.type === "error" || toast.type === "success" ? toast.type : "info"}
            onClose={clearToast}
          >
            {toast.message}
          </SnackBar>
        </Snackbar>
      )}
    </Box>
  );
}
